/* eslint-disable react-native/no-inline-styles */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, TVEventHandler, View } from 'react-native';
import debounce from 'lodash/debounce';

import { pictureDetailedVODPageTestIDs, Theme } from '@constants';
import { useContextNodeHandle } from '@hooks';

import { PictureDetailedEpisodesRow } from './PictureDetailedEpisodesRow';
import { PictureDetailedSeasonsSelector } from './PictureDetailedSeasonsSelector';

import { isAndroid, isWeb } from '../../../helpers/Platform';

const FIRST_SEASON_INDEX = 0;
const FIRST_EPISODE_INDEX = 0;
const FOCUS_TIME_OUT = 10;

const getSeasonIndex = (seasons = [], currentItem = null) => {
  if (Array.isArray(seasons) && currentItem) {
    return seasons.findIndex(item => item.id === currentItem.seasonID);
  }

  return 0;
};

const getEpisodeIndex = (seasons = [], currentItem = null) => {
  if (Array.isArray(seasons) && currentItem) {
    const currentSeason = seasons.find(item => item.id === currentItem.seasonID);

    if (currentSeason) {
      return currentSeason?.episodes?.findIndex(item => item.id === currentItem.id);
    }
  }
  return 0;
};

export const PictureDetailedPageLayout = ({
  testID,
  data,
  upNextShow,
  nextFocusUp,
  onLayoutFocus,
  onFocus,
  onBlur,
  isShowingHeader,
}) => {
  const isFocused = !isShowingHeader;
  const isEpisodeSelectorFocused = useRef(false);
  const hideSeasonFocus = useRef(false);
  const hideEpisodeFocus = useRef(false);
  const episodesListReference = useRef();
  const seasonsListReference = useRef();

  const lastSeasonIndex = data?.seasons ? data?.seasons?.length - 1 : 0;
  const firstSeasonAndEpisode = data?.seasons ? data?.seasons[0]?.episodes[0] : null;

  const continueWatching = useRef(firstSeasonAndEpisode);

  // parse season and episode
  const nextSeasonNumber = getSeasonIndex(data?.seasons, upNextShow ?? firstSeasonAndEpisode);
  const nextEpisodeNumber = getEpisodeIndex(data?.seasons, upNextShow ?? firstSeasonAndEpisode);

  // Focus current season on Seasons list.
  const [currentSeasonIndex, setCurrentSeasonIndex] = useState(nextSeasonNumber);
  // Focus current episode on Episodes list.
  const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(nextEpisodeNumber);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentSeason = data?.seasons ? data.seasons[currentSeasonIndex] : [];

  const setSeasonIndex = index => {
    const scrolling = () => {
      if (!isEpisodeSelectorFocused.current) {
        hideEpisodeFocus.current = true;
        setCurrentEpisodeIndex(0);

        scrollToEpisodeIndex(0, 0, { animated: false, focusToIndex: true }, true);
        hideEpisodeFocus.current = false;

        scrollToSeasonIndex(
          index,
          index,
          {
            animated: true,
            focusToIndex: true,
          },
          true,
        );

        // Force focus the season selector
        if (isAndroid) {
          setTimeout(() => {
            scrollToSeasonIndex(
              index,
              index,
              {
                animated: true,
                focusToIndex: true,
              },
              true,
            );
          }, FOCUS_TIME_OUT);
        }
      }
    };

    // Prevents focus for the first episode index when switching between seasons.
    if (currentEpisodeIndex === FIRST_EPISODE_INDEX) {
      setCurrentSeasonIndex(index);
    } else {
      setTimeout(() => {
        scrolling();
      }, FOCUS_TIME_OUT);

      setCurrentSeasonIndex(index);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandler = useCallback(debounce(setSeasonIndex, 400), [currentEpisodeIndex, currentSeasonIndex]);

  // Use the flatlist reference of episode selector and focus/scrolls to index.
  const scrollToEpisodeIndex = (
    nextIndex,
    newFocusPos,
    { animated = false, focusToIndex = false },
    forceFocus = false,
  ) => {
    if (focusToIndex) {
      episodesListReference.current.focusToIndex(newFocusPos, forceFocus);
    }
    episodesListReference.current.scrollToIndex(nextIndex, animated);
  };

  // Use the flatlist reference of season selector and focus/scrolls to index.
  const scrollToSeasonIndex = (
    nextIndex,
    newFocusPos,
    { animated = false, focusToIndex = false },
    forceFocus = false,
  ) => {
    if (focusToIndex) {
      seasonsListReference.current.focusToIndex(newFocusPos, forceFocus);
    }
    seasonsListReference.current.scrollToIndex(nextIndex, animated);
  };

  // Focus the next or previos season and episode
  const continuousScrolling = ({ nextSeasonIndex, nextEpisodeIndex }) => {
    const scrolling = () => {
      scrollToSeasonIndex(
        nextSeasonIndex,
        nextSeasonIndex,
        {
          animated: true,
          focusToIndex: true,
        },
        true,
      );
      scrollToEpisodeIndex(
        nextEpisodeIndex,
        nextEpisodeIndex,
        {
          animated: true,
          focusToIndex: true,
        },
        true,
      );

      // Force focus the next episode
      if (isAndroid) {
        setTimeout(() => {
          scrollToEpisodeIndex(
            nextEpisodeIndex,
            nextEpisodeIndex,
            {
              animated: true,
              focusToIndex: true,
            },
            true,
          );
        }, FOCUS_TIME_OUT);
      }
    };

    setTimeout(() => {
      scrolling();
    }, FOCUS_TIME_OUT);

    setCurrentSeasonIndex(nextSeasonIndex);
    setCurrentEpisodeIndex(nextEpisodeIndex);
  };

  const scrollHandler = keyEvent => {
    if (isFocused) {
      const key = keyEvent.key;

      switch (key) {
        // Previous season
        case 'ArrowLeft': {
          // Scrolls to the previous season if it is not the first one
          // and if currentEpisodeIndex is the first one
          if (
            isEpisodeSelectorFocused.current &&
            currentSeasonIndex !== FIRST_SEASON_INDEX &&
            currentEpisodeIndex === FIRST_EPISODE_INDEX
          ) {
            const nextSeasonIndex = currentSeasonIndex - 1;
            const nextEpisodeIndex = data?.seasons?.[nextSeasonIndex].episodes.length - 1;

            continuousScrolling({ nextSeasonIndex, nextEpisodeIndex });
          }
          break;
        }
        // Next season
        case 'ArrowRight': {
          // Scrolls to the next season if it is not the last season
          // and if currentEpisodeIndex is the last one of the current season
          const lasEpisodeIndex = data?.seasons?.[currentSeasonIndex].episodes.length - 1;

          if (
            isEpisodeSelectorFocused.current &&
            currentSeasonIndex < lastSeasonIndex &&
            currentEpisodeIndex === lasEpisodeIndex
          ) {
            const nextSeasonIndex = currentSeasonIndex + 1;
            const nextEpisodeIndex = 0;

            continuousScrolling({ nextSeasonIndex, nextEpisodeIndex });
          }
          break;
        }
      }
    }
  };

  const episodeTilesHandle = useContextNodeHandle(pictureDetailedVODPageTestIDs.episodesTiles);

  const seasonsMenuHandle = useContextNodeHandle(pictureDetailedVODPageTestIDs.seasonsMenu);

  // Catch keys events for right and left
  useEffect(() => {
    const tvEventHandler = new TVEventHandler();

    if (isWeb) {
      document.addEventListener('keydown', scrollHandler, true);
    } else {
      const handleEvent = (cmp, event) => {
        if (
          isFocused &&
          event &&
          event.eventType &&
          (event.eventType === 'left' || event.eventType === 'right') &&
          event.eventKeyAction === 0
        ) {
          scrollHandler({
            key: event.eventType === 'left' ? 'ArrowLeft' : 'ArrowRight',
          });
        }
      };

      tvEventHandler.enable(null, handleEvent);
    }
    return () => {
      if (isWeb) {
        document.removeEventListener('keydown', scrollHandler, true);
      } else {
        tvEventHandler?.disable();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused, currentEpisodeIndex, currentSeasonIndex, isEpisodeSelectorFocused]);

  // Focus to the next season and episode by default from upNextShow data
  useEffect(() => {
    if (
      upNextShow &&
      currentSeason?.episodes?.length > 0 &&
      episodesListReference.current &&
      seasonsListReference.current
    ) {
      const { season, episode } = continueWatching.current || {};
      let { season: nextSeason, episode: nextEpisode } = upNextShow;

      // Focus when next season and episode is different
      if (nextSeason !== season || nextEpisode !== episode) {
        hideSeasonFocus.current = true;
        continueWatching.current = upNextShow;
        nextSeason = getSeasonIndex(data?.seasons, upNextShow);
        nextEpisode = getEpisodeIndex(data?.seasons, upNextShow);

        // Focus next season
        setTimeout(() => {
          scrollToSeasonIndex(nextSeason, nextSeason, {
            animated: false,
            focusToIndex: true,
          });

          // Focus next episode
          setTimeout(() => {
            scrollToEpisodeIndex(nextEpisode, nextEpisode, {
              animated: false,
              focusToIndex: true,
            });

            hideSeasonFocus.current = false;
          }, FOCUS_TIME_OUT);
        }, FOCUS_TIME_OUT);
        setCurrentSeasonIndex(nextSeason);
        setCurrentEpisodeIndex(nextEpisode);
      }
    }
  }, [upNextShow, currentSeason, data]);

  return (
    <View
      style={[
        styles.layout,
        {
          backgroundColor: isShowingHeader ? Theme.withOpacity(Theme.blackPearlBlue, 0.7) : Theme.transparent,
        },
      ]}
      testID={testID}
    >
      <View
        style={{
          marginTop: isShowingHeader ? 0 : Theme.px(190),
        }}
      />
      <PictureDetailedSeasonsSelector
        currentSeason={data?.seasons ? currentSeasonIndex : null}
        data={data?.seasons ?? []}
        focusIndexChanged={debouncedHandler}
        hideFocus={hideSeasonFocus.current}
        nextFocusDown={episodeTilesHandle}
        nextFocusUp={nextFocusUp}
        testID={pictureDetailedVODPageTestIDs.seasonsMenu}
        onBlur={onBlur}
        onFlatlistRef={reference => {
          seasonsListReference.current = reference;
        }}
        onFocus={onFocus}
        onLayoutFocus={onLayoutFocus}
      />
      <PictureDetailedEpisodesRow
        currentEpisode={currentSeason?.episodes ? currentEpisodeIndex : null}
        data={currentSeason}
        focusIndexChanged={index => {
          setCurrentEpisodeIndex(index);
        }}
        hideFocus={hideEpisodeFocus.current}
        nextFocusUp={seasonsMenuHandle}
        playlist={data?.seasons}
        testID={pictureDetailedVODPageTestIDs.episodesTiles}
        onBlur={onBlur}
        onFlatlistRef={reference => {
          episodesListReference.current = reference;
        }}
        onFocus={onFocus}
        onListFocus={focus => {
          isEpisodeSelectorFocused.current = focus;
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  layout: {
    height: Theme.appHeight,
    width: Theme.appWidth,
  },
  text: {
    marginBottom: Theme.px(9),
  },
});
